import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Toast from '../components/Toast';
import { ImpersonationContext, ImpersonationSessionState } from '../contexts/ImpersonationContext';
import ImpersonationService from '../services/impersonation.service';
import { LOCAL_STORAGE_KEYS } from '../utils/appConstant';
import { Modules } from '../enums';

interface ImpersonationProviderProps {
  children: React.ReactNode;
}

const readStoredSession = (): ImpersonationSessionState | null => {
  const raw = sessionStorage.getItem(LOCAL_STORAGE_KEYS.impersonationSession);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ImpersonationSessionState;
    // AC7/ST-007: an already-expired session in sessionStorage is silently cleared —
    // it already ended, no API call needed.
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      sessionStorage.removeItem(LOCAL_STORAGE_KEYS.impersonationSession);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(LOCAL_STORAGE_KEYS.impersonationSession);
    return null;
  }
};

const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [session, setSession] = useState<ImpersonationSessionState | null>(readStoredSession);

  const startSession = useCallback((next: ImpersonationSessionState) => {
    sessionStorage.setItem(LOCAL_STORAGE_KEYS.impersonationSession, JSON.stringify(next));
    setSession(next);
  }, []);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem(LOCAL_STORAGE_KEYS.impersonationSession);
    setSession(null);
  }, []);

  const endSession = useCallback(async () => {
    const current = session ?? readStoredSession();
    if (!current) return;
    try {
      await ImpersonationService.end(current.impersonationToken);
    } finally {
      clearSession();
      toast.info(<Toast message="Impersonation session ended" />, { icon: false });
      navigate(`${Modules.tenants}/${current.targetTenantId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, clearSession, navigate]);

  // AC5: auto-end when the timer elapses, checked once a second by the banner's own
  // countdown — this effect is the fallback that fires even if the banner unmounts.
  useEffect(() => {
    if (!session) return undefined;
    const msRemaining = new Date(session.expiresAt).getTime() - Date.now();
    if (msRemaining <= 0) {
      endSession();
      return undefined;
    }
    const timeout = setTimeout(() => endSession(), msRemaining);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId]);

  const value = useMemo(
    () => ({ session, active: !!session, startSession, endSession }),
    [session, startSession, endSession]
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
};

export default ImpersonationProvider;
